import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Hash, ChevronLeft, Bell, BellOff, Calendar, Sun, Moon, Star, Loader2, Sparkles, AlertCircle, Check, Zap } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import { toast } from 'sonner';
import { 
  requestNotificationPermission, 
  getNotificationPermission, 
  showLocalNotification,
  isPushSupported 
} from '@/utils/notifications';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALERT_TYPES = [
  {
    id: 'personal_year',
    name: 'Année personnelle',
    description: 'Notification au changement d\'année personnelle (1er janvier)',
    icon: Sun,
    color: 'amber'
  },
  {
    id: 'personal_month',
    name: 'Mois personnel',
    description: 'Notification au début de chaque mois',
    icon: Moon,
    color: 'purple'
  },
  {
    id: 'personal_day',
    name: 'Message quotidien',
    description: 'Vibration du jour chaque matin',
    icon: Star,
    color: 'emerald'
  }
];

const VibrationCard = ({ type, number, theme, description, icon: Icon, color }) => {
  const colorClasses = {
    amber: 'from-amber-500 to-orange-500 bg-amber-50 border-amber-200 text-amber-700',
    purple: 'from-purple-500 to-indigo-500 bg-purple-50 border-purple-200 text-purple-700',
    emerald: 'from-emerald-500 to-teal-500 bg-emerald-50 border-emerald-200 text-emerald-700'
  };
  
  return (
    <div className={`p-4 rounded-xl border-2 ${colorClasses[color].split(' ').slice(1).join(' ')}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorClasses[color].split(' ').slice(0, 2).join(' ')} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <span className="text-2xl font-bold">{number}</span>
          <p className="text-sm opacity-75">{type}</p>
        </div>
      </div>
      {theme && <p className="text-sm font-medium">{theme}</p>}
      {description && <p className="text-xs opacity-75 mt-1">{description}</p>}
    </div>
  );
};

const NumerologiaAlertsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [vibrations, setVibrations] = useState(null);
  const [birthDate, setBirthDate] = useState('');
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState(['personal_year', 'personal_month']);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [dailyMessage, setDailyMessage] = useState(null);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill birth date from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled && user.birth_date) {
      setBirthDate(user.birth_date);
      setProfilePreFilled(true);
    }
  }, [isAuthenticated, user, profilePreFilled]);

  // Check notification permission
  useEffect(() => {
    if (isPushSupported()) {
      setNotificationPermission(getNotificationPermission());
    }
  }, []);

  // Load subscription status
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchSubscription = async () => {
    try {
      const userId = user.id || user._id;
      const response = await fetch(`${API_URL}/api/numerologia/alerts/${userId}`);
      const data = await response.json();
      
      if (data.success && data.subscribed) {
        setSubscription(data.subscription);
        setVibrations(data.current_vibrations);
        setBirthDate(data.subscription.birth_date);
        setAlertsEnabled(data.subscription.alerts_enabled);
        setSelectedAlertTypes(data.subscription.alert_types || ['personal_year', 'personal_month']);
        
        // Also fetch daily message
        fetchDailyMessage(userId);
        // Check for pending alerts
        checkAlerts(userId);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyMessage = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/numerologia/alerts/daily-message/${userId}`);
      const data = await response.json();
      
      if (data.success) {
        setDailyMessage(data.daily_message);
      }
    } catch (error) {
      console.error('Error fetching daily message:', error);
    }
  };

  const checkAlerts = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/numerologia/alerts/check/${userId}`);
      const data = await response.json();
      
      if (data.success && data.alerts?.length > 0) {
        setPendingAlerts(data.alerts);
        
        // Show notifications for new alerts
        if (notificationPermission === 'granted') {
          data.alerts.forEach(alert => {
            showLocalNotification(alert.title, {
              body: alert.message,
              tag: `numerologia-${alert.type}`,
              data: { url: '/numerologia/alerts' }
            });
          });
        }
      }
    } catch (error) {
      console.error('Error checking alerts:', error);
    }
  };

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    setNotificationPermission(granted ? 'granted' : 'denied');
    
    if (granted) {
      toast.success('Notifications activées !');
    } else {
      toast.error('Notifications refusées');
    }
  };

  const handleSubscribe = async () => {
    if (!birthDate) {
      toast.error('Veuillez entrer votre date de naissance');
      return;
    }
    
    setSaving(true);
    
    try {
      const userId = user.id || user._id;
      const response = await fetch(`${API_URL}/api/numerologia/alerts/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          birth_date: birthDate,
          first_name: user.first_name || user.name || 'Ami',
          alerts_enabled: alertsEnabled,
          alert_types: selectedAlertTypes
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVibrations(data.current_vibrations);
        toast.success('Alertes vibratoires activées !');
        fetchSubscription();
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Erreur lors de l\'activation');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreferences = async () => {
    setSaving(true);
    
    try {
      const userId = user.id || user._id;
      const response = await fetch(`${API_URL}/api/numerologia/alerts/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alerts_enabled: alertsEnabled,
          alert_types: selectedAlertTypes
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Préférences mises à jour');
      }
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!confirm('Désactiver toutes les alertes vibratoires ?')) return;
    
    try {
      const userId = user.id || user._id;
      const response = await fetch(`${API_URL}/api/numerologia/alerts/${userId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubscription(null);
        setVibrations(null);
        toast.success('Alertes désactivées');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Erreur');
    }
  };

  const toggleAlertType = (typeId) => {
    if (selectedAlertTypes.includes(typeId)) {
      setSelectedAlertTypes(selectedAlertTypes.filter(t => t !== typeId));
    } else {
      setSelectedAlertTypes([...selectedAlertTypes, typeId]);
    }
  };

  const testNotification = () => {
    if (notificationPermission === 'granted') {
      showLocalNotification('Test Numerologia', {
        body: 'Vos notifications fonctionnent correctement !',
        tag: 'test-notification'
      });
      toast.success('Notification envoyée !');
    } else {
      toast.error('Autorisez d\'abord les notifications');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center p-8">
          <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold text-gray-700 mb-2">Connexion requise</h2>
          <p className="text-gray-500 mb-4">Connectez-vous pour gérer vos alertes</p>
          <Button onClick={() => navigate('/auth')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/numerologia')}
            className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-600" />
            <span className="font-heading text-lg font-bold text-emerald-700">
              Alertes Vibratoires
            </span>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
              <p className="text-gray-500">Chargement...</p>
            </div>
          )}

          {/* Notification Permission Banner */}
          {!loading && isPushSupported() && notificationPermission !== 'granted' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-amber-50 rounded-xl border border-amber-200"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">Activez les notifications</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Pour recevoir vos alertes vibratoires, autorisez les notifications du navigateur.
                  </p>
                  <Button
                    onClick={handleRequestPermission}
                    size="sm"
                    className="mt-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Autoriser
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Current Vibrations (if subscribed) */}
          {!loading && vibrations && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <h2 className="font-heading text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Vos vibrations actuelles
              </h2>
              
              <div className="grid gap-4">
                <VibrationCard
                  type="Année personnelle"
                  number={vibrations.personal_year?.number}
                  theme={vibrations.personal_year?.meaning?.theme}
                  description={vibrations.personal_year?.meaning?.description}
                  icon={Sun}
                  color="amber"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <VibrationCard
                    type="Mois personnel"
                    number={vibrations.personal_month?.number}
                    icon={Moon}
                    color="purple"
                  />
                  <VibrationCard
                    type="Jour personnel"
                    number={vibrations.personal_day?.number}
                    icon={Star}
                    color="emerald"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Daily Message */}
          {!loading && dailyMessage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-6 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-2xl"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                <h3 className="font-heading font-semibold text-emerald-800">Message du jour</h3>
              </div>
              <p className="text-emerald-700">{dailyMessage.message}</p>
              {dailyMessage.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {dailyMessage.keywords.map((kw, i) => (
                    <span key={i} className="px-2 py-1 bg-white/50 text-emerald-700 rounded-full text-xs">
                      {kw}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Pending Alerts */}
          {pendingAlerts.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              <h3 className="font-heading font-semibold text-gray-800">Nouvelles alertes</h3>
              {pendingAlerts.map((alert, i) => (
                <div key={i} className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <h4 className="font-semibold text-purple-800">{alert.title}</h4>
                  <p className="text-sm text-purple-700 mt-1">{alert.message}</p>
                  {alert.advice && (
                    <p className="text-xs text-purple-600 mt-2 italic">{alert.advice}</p>
                  )}
                </div>
              ))}
            </motion.div>
          )}

          {/* Subscription Form / Settings */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
            >
              <h2 className="font-heading text-lg font-semibold text-gray-800 mb-4">
                {subscription ? 'Paramètres des alertes' : 'Activer les alertes'}
              </h2>
              
              {/* Birth Date Input */}
              {!subscription && (
                <div className="mb-6">
                  <Label htmlFor="birthDate">Date de naissance</Label>
                  <Input
                    id="birthDate"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
              
              {/* Enable/Disable Switch */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl mb-4">
                <div className="flex items-center gap-3">
                  {alertsEnabled ? (
                    <Bell className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <BellOff className="h-5 w-5 text-gray-400" />
                  )}
                  <div>
                    <p className="font-medium text-gray-800">Alertes vibratoires</p>
                    <p className="text-xs text-gray-500">Recevoir des notifications</p>
                  </div>
                </div>
                <Switch
                  checked={alertsEnabled}
                  onCheckedChange={setAlertsEnabled}
                />
              </div>
              
              {/* Alert Types */}
              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700">Types d'alertes</p>
                {ALERT_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedAlertTypes.includes(type.id);
                  
                  return (
                    <div
                      key={type.id}
                      onClick={() => toggleAlertType(type.id)}
                      className={`
                        p-3 rounded-xl border-2 cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-emerald-400 bg-emerald-50' 
                          : 'border-gray-200 hover:border-emerald-200'}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-8 h-8 rounded-lg flex items-center justify-center
                          ${isSelected ? 'bg-emerald-500' : 'bg-gray-200'}
                        `}>
                          {isSelected ? (
                            <Check className="h-4 w-4 text-white" />
                          ) : (
                            <Icon className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-700'}`}>
                            {type.name}
                          </p>
                          <p className="text-xs text-gray-500">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {subscription ? (
                  <>
                    <Button
                      onClick={handleUpdatePreferences}
                      disabled={saving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      Enregistrer les préférences
                    </Button>
                    
                    {notificationPermission === 'granted' && (
                      <Button
                        variant="outline"
                        onClick={testNotification}
                        className="w-full rounded-xl"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Tester une notification
                      </Button>
                    )}
                    
                    <Button
                      variant="ghost"
                      onClick={handleUnsubscribe}
                      className="w-full text-red-500 hover:bg-red-50 rounded-xl"
                    >
                      <BellOff className="h-4 w-4 mr-2" />
                      Désactiver toutes les alertes
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleSubscribe}
                    disabled={saving || !birthDate}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-6"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Bell className="h-4 w-4 mr-2" />
                    )}
                    Activer les alertes vibratoires
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* Info */}
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-sm text-gray-600">
              Les alertes vibratoires vous préviennent quand vos cycles numériques changent,
              vous permettant d'ajuster votre énergie en conséquence.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumerologiaAlertsPage;
